import { Button, Col, Flex, Form, Modal, Row } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { PhoneInput } from "../../../components/PhoneInputs/PhoneInput.tsx"
import { placeholders } from "../../../constants/placeholders.ts"
import { validatePhone } from "../../../utils/validation/validate-phone.ts"
import type { CreateSupplierData } from "../api/services.types.ts"
import type { SupplierListItem } from "../types/types.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"

interface SupplierFormModalProps {
  loading?: boolean
  saving?: boolean
  initialValues: SupplierListItem | null
  /** The name the caller already has for this supplier, if any. */
  defaultName?: string
  onSave: (data: CreateSupplierData) => void
  onCancel: () => void
}

export const SupplierFormModal = ({
  loading,
  saving,
  initialValues,
  defaultName,
  onSave,
  onCancel
}: SupplierFormModalProps) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<CreateSupplierData>()
  const isEdit = Boolean(initialValues)

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        first_name: initialValues.first_name,
        middle_name: initialValues.middle_name,
        last_name: initialValues.last_name,
        email: initialValues.email,
        phone: initialValues.phone,
        address: initialValues.address,
        notes: initialValues.notes
      })
    } else {
      form.resetFields()
      if (defaultName) {
        // Seeded whole rather than split: a supplier is as often a company
        // ("Acme Roofing GmbH") as a person, and any split of that is a guess.
        // The user sees the form before saving and can move the parts.
        form.setFieldValue("first_name", defaultName)
      }
    }
  }, [initialValues, defaultName, form])

  return (
    <Modal
      open
      centered
      maskClosable={false}
      title={
        isEdit ? t("suppliers.editModalTitle") : t("suppliers.addModalTitle")
      }
      onCancel={onCancel}
      footer={null}
      width={640}
    >
      <Form<CreateSupplierData>
        form={form}
        layout="vertical"
        onFinish={onSave}
        disabled={loading}
      >
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              label={t("suppliers.form.firstName")}
              name="first_name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("suppliers.form.firstNameRequired")
                }
              ]}
            >
              <TextInput
                placeholder={t("suppliers.form.firstNamePlaceholder")}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label={t("suppliers.form.middleName")}
              name="middle_name"
            >
              <TextInput
                placeholder={t("suppliers.form.middleNamePlaceholder")}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t("suppliers.form.lastName")} name="last_name">
              <TextInput
                placeholder={t("suppliers.form.lastNamePlaceholder")}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("suppliers.form.email")}
              name="email"
              rules={[
                { type: "email", message: t("suppliers.form.emailInvalid") }
              ]}
            >
              <TextInput
                disableAutoCapitalize
                placeholder={t("suppliers.form.emailPlaceholder")}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={t("suppliers.form.phone")}
              name="phone"
              rules={[{ validator: validatePhone }]}
            >
              <PhoneInput placeholder={placeholders.phone} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={t("suppliers.form.address")} name="address">
          <TextInput
            placeholder={t("suppliers.form.addressPlaceholder")}
            size="large"
          />
        </Form.Item>
        <Form.Item label={t("suppliers.form.notes")} name="notes">
          <TextArea
            placeholder={t("suppliers.form.notesPlaceholder")}
            rows={3}
          />
        </Form.Item>
        <Flex justify="flex-end" gap="middle">
          <Button size="large" onClick={onCancel}>
            {t("suppliers.cancel")}
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            loading={saving}
          >
            {isEdit ? t("suppliers.saveChanges") : t("suppliers.addSupplier")}
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}
